/**
 * Effects Panel component
 * Controls for lighting effects, filters, and restoration
 */

import { useState } from 'react';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Slider,
  Button,
  Card,
  CardContent,
  cn,
} from '@frameit/ui';
import { useProjectStore, useSelectedArtwork } from '../../stores/projectStore';
import {
  EFFECT_PRESETS,
  EFFECT_CATEGORIES,
  getEffectPreset,
  getEffectDefaults,
  useEffectThumbnails,
  type EffectCategory,
} from './effects';
import { getLocalImageUrl, storeLocalImage } from '../../stores/projectStore';
import { BackgroundRemoval } from './BackgroundRemoval';

export interface EffectsPanelProps {
  /** Custom class name */
  className?: string;
}

/** Effects panel component */
export function EffectsPanel({ className }: EffectsPanelProps) {
  const selectedArtwork = useSelectedArtwork();
  const updateRestoration = useProjectStore((s) => s.updateRestoration);
  const updateColorGrading = useProjectStore((s) => s.updateColorGrading);
  const updateDisplayScale = useProjectStore((s) => s.updateDisplayScale);
  const addEffect = useProjectStore((s) => s.addEffect);
  const removeEffect = useProjectStore((s) => s.removeEffect);
  const updateEffect = useProjectStore((s) => s.updateEffect);

  const [effectCategory, setEffectCategory] = useState<EffectCategory | 'all'>('all');

  // Generate effect preview thumbnails from current artwork
  const artworkImageUrl = selectedArtwork ? getLocalImageUrl(selectedArtwork.artworkId) : null;
  const effectThumbnails = useEffectThumbnails(artworkImageUrl ?? null);

  if (!selectedArtwork) {
    return (
      <div className={cn('p-4 text-center text-muted-foreground', className)}>
        Select an artwork to apply effects
      </div>
    );
  }

  const restoration = selectedArtwork.restoration || {};
  const colorGrading = selectedArtwork.colorGrading || {};

  return (
    <div className={cn('flex flex-col h-full overflow-auto', className)}>
      <Tabs defaultValue="color" className="flex-1">
        <TabsList className="w-full justify-start px-4">
          <TabsTrigger value="color">Color</TabsTrigger>
          <TabsTrigger value="restoration">Restore</TabsTrigger>
          <TabsTrigger value="effects">Effects</TabsTrigger>
          <TabsTrigger value="tools">Tools</TabsTrigger>
        </TabsList>

        {/* Color Grading */}
        <TabsContent value="color" className="p-4 space-y-4">
          <h4 className="text-sm font-medium">Color Adjustments</h4>

          {/* Artwork size */}
          <div>
            <label className="text-xs text-muted-foreground">Artwork Size</label>
            <Slider
              value={selectedArtwork.displayScale ?? 1}
              min={0.5}
              max={2}
              step={0.05}
              onChange={(value) => updateDisplayScale(selectedArtwork.id, value)}
              showValue
              formatValue={(v) => `${Math.round(v * 100)}%`}
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Temperature</label>
            <Slider
              value={colorGrading.temperature ?? 0}
              min={-100}
              max={100}
              step={1}
              onChange={(value) => updateColorGrading(selectedArtwork.id, { temperature: value })}
              showValue
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Tint</label>
            <Slider
              value={colorGrading.tint ?? 0}
              min={-100}
              max={100}
              step={1}
              onChange={(value) => updateColorGrading(selectedArtwork.id, { tint: value })}
              showValue
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Contrast</label>
            <Slider
              value={colorGrading.contrast ?? 0}
              min={-100}
              max={100}
              step={1}
              onChange={(value) => updateColorGrading(selectedArtwork.id, { contrast: value })}
              showValue
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Saturation</label>
            <Slider
              value={colorGrading.saturation ?? 0}
              min={-100}
              max={100}
              step={1}
              onChange={(value) => updateColorGrading(selectedArtwork.id, { saturation: value })}
              showValue
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Vibrance</label>
            <Slider
              value={colorGrading.vibrance ?? 0}
              min={-100}
              max={100}
              step={1}
              onChange={(value) => updateColorGrading(selectedArtwork.id, { vibrance: value })}
              showValue
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Highlights</label>
            <Slider
              value={colorGrading.highlights ?? 0}
              min={-100}
              max={100}
              step={1}
              onChange={(value) => updateColorGrading(selectedArtwork.id, { highlights: value })}
              showValue
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Shadows</label>
            <Slider
              value={colorGrading.shadows ?? 0}
              min={-100}
              max={100}
              step={1}
              onChange={(value) => updateColorGrading(selectedArtwork.id, { shadows: value })}
              showValue
            />
          </div>

          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() =>
              updateColorGrading(selectedArtwork.id, {
                temperature: 0,
                tint: 0,
                contrast: 0,
                saturation: 0,
                vibrance: 0,
                highlights: 0,
                shadows: 0,
              })
            }
          >
            Reset Colors
          </Button>
        </TabsContent>

        {/* Restoration */}
        <TabsContent value="restoration" className="p-4 space-y-4">
          <h4 className="text-sm font-medium">Image Restoration</h4>

          {/* Wrinkle removal */}
          <Card>
            <CardContent className="p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Remove Wrinkles</span>
                <input
                  type="checkbox"
                  checked={restoration.removeWrinkles?.enabled ?? false}
                  onChange={(e) =>
                    updateRestoration(selectedArtwork.id, {
                      removeWrinkles: {
                        ...restoration.removeWrinkles,
                        enabled: e.target.checked,
                        strength: restoration.removeWrinkles?.strength ?? 0.5,
                      },
                    })
                  }
                  className="h-5 w-5"
                />
              </div>
              {restoration.removeWrinkles?.enabled && (
                <Slider
                  value={restoration.removeWrinkles.strength}
                  min={0}
                  max={1}
                  step={0.05}
                  onChange={(value) =>
                    updateRestoration(selectedArtwork.id, {
                      removeWrinkles: {
                        ...restoration.removeWrinkles!,
                        strength: value,
                      },
                    })
                  }
                  showValue
                  formatValue={(v) => `${Math.round(v * 100)}%`}
                />
              )}
            </CardContent>
          </Card>

          {/* Color restoration */}
          <Card>
            <CardContent className="p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Restore Colors</span>
                <input
                  type="checkbox"
                  checked={restoration.restoreColors?.enabled ?? false}
                  onChange={(e) =>
                    updateRestoration(selectedArtwork.id, {
                      restoreColors: {
                        ...restoration.restoreColors,
                        enabled: e.target.checked,
                      },
                    })
                  }
                  className="h-5 w-5"
                />
              </div>
              {restoration.restoreColors?.enabled && (
                <>
                  <div className="flex items-center justify-between text-xs">
                    <span>Auto White Balance</span>
                    <input
                      type="checkbox"
                      checked={restoration.restoreColors.autoWhiteBalance ?? false}
                      onChange={(e) =>
                        updateRestoration(selectedArtwork.id, {
                          restoreColors: {
                            ...restoration.restoreColors!,
                            autoWhiteBalance: e.target.checked,
                          },
                        })
                      }
                      className="h-5 w-5"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Remove Yellowing</label>
                    <Slider
                      value={restoration.restoreColors.deyellow ?? 0}
                      min={0}
                      max={1}
                      step={0.05}
                      onChange={(value) =>
                        updateRestoration(selectedArtwork.id, {
                          restoreColors: {
                            ...restoration.restoreColors!,
                            deyellow: value,
                          },
                        })
                      }
                      showValue
                      formatValue={(v) => `${Math.round(v * 100)}%`}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Sharpen */}
          <Card>
            <CardContent className="p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Sharpen</span>
                <input
                  type="checkbox"
                  checked={restoration.sharpen?.enabled ?? false}
                  onChange={(e) =>
                    updateRestoration(selectedArtwork.id, {
                      sharpen: {
                        ...restoration.sharpen,
                        enabled: e.target.checked,
                        amount: restoration.sharpen?.amount ?? 0.5,
                      },
                    })
                  }
                  className="h-5 w-5"
                />
              </div>
              {restoration.sharpen?.enabled && (
                <Slider
                  value={restoration.sharpen.amount}
                  min={0}
                  max={2}
                  step={0.1}
                  onChange={(value) =>
                    updateRestoration(selectedArtwork.id, {
                      sharpen: {
                        ...restoration.sharpen!,
                        amount: value,
                      },
                    })
                  }
                  showValue
                  formatValue={(v) => v.toFixed(1)}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Effects */}
        <TabsContent value="effects" className="p-4 space-y-4">
          {/* Active effects list */}
          {selectedArtwork.effects.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Active Effects</h4>
              {selectedArtwork.effects.map((effect, index) => {
                const preset = getEffectPreset(effect.templateId);
                if (!preset) return null;
                return (
                  <Card key={`${effect.templateId}-${index}`}>
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{preset.name}</span>
                        <div className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            checked={effect.enabled}
                            onChange={(e) =>
                              updateEffect(selectedArtwork.id, index, { enabled: e.target.checked })
                            }
                            className="h-5 w-5"
                          />
                          <button
                            onClick={() => removeEffect(selectedArtwork.id, index)}
                            className="text-muted-foreground hover:text-destructive transition-colors ml-1"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                      {/* Effect parameters */}
                      {effect.enabled &&
                        preset.params.map((param) => (
                          <div key={param.key}>
                            <label className="text-xs text-muted-foreground">{param.label}</label>
                            <Slider
                              value={
                                typeof effect.params?.[param.key] === 'number'
                                  ? (effect.params[param.key] as number)
                                  : param.defaultValue
                              }
                              min={param.min}
                              max={param.max}
                              step={param.step}
                              onChange={(value) =>
                                updateEffect(selectedArtwork.id, index, {
                                  params: { ...effect.params, [param.key]: value },
                                })
                              }
                              showValue
                            />
                          </div>
                        ))}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Effect preset grid */}
          <div>
            <h4 className="text-sm font-medium mb-2">Add Effect</h4>
            {/* Category filter */}
            <div className="flex gap-1 mb-3 flex-wrap">
              {EFFECT_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setEffectCategory(cat.id as EffectCategory | 'all')}
                  className={cn(
                    'px-2.5 py-1 text-xs rounded-full border transition-colors',
                    effectCategory === cat.id
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background border-border hover:bg-accent',
                  )}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Preset grid */}
            <div className="grid grid-cols-3 gap-2">
              {EFFECT_PRESETS.filter(
                (p) => effectCategory === 'all' || p.category === effectCategory,
              ).map((preset) => {
                const isActive = selectedArtwork.effects.some(
                  (e) => e.templateId === preset.id && e.enabled,
                );
                return (
                  <button
                    key={preset.id}
                    onClick={() => {
                      if (isActive) {
                        // Remove if already active
                        const idx = selectedArtwork.effects.findIndex(
                          (e) => e.templateId === preset.id,
                        );
                        if (idx >= 0) removeEffect(selectedArtwork.id, idx);
                      } else {
                        // Add effect with defaults
                        addEffect(selectedArtwork.id, {
                          templateId: preset.id,
                          enabled: true,
                          params: getEffectDefaults(preset),
                        });
                      }
                    }}
                    className={cn(
                      'flex flex-col items-center gap-1 p-2 rounded-lg border text-xs transition-all',
                      'hover:bg-accent focus:outline-none focus:ring-2 focus:ring-primary',
                      isActive ? 'border-primary bg-primary/10 text-primary' : 'border-border',
                    )}
                  >
                    {/* Effect preview thumbnail */}
                    <div
                      className={cn(
                        'w-12 h-12 rounded-md overflow-hidden flex items-center justify-center',
                        !effectThumbnails.get(preset.id) && 'bg-gradient-to-br',
                        !effectThumbnails.get(preset.id) &&
                          preset.category === 'color' &&
                          'from-orange-200 to-pink-200',
                        !effectThumbnails.get(preset.id) &&
                          preset.category === 'lighting' &&
                          'from-yellow-200 to-amber-200',
                        !effectThumbnails.get(preset.id) &&
                          preset.category === 'texture' &&
                          'from-stone-200 to-gray-300',
                        !effectThumbnails.get(preset.id) &&
                          preset.category === 'artistic' &&
                          'from-purple-200 to-blue-200',
                      )}
                    >
                      {effectThumbnails.get(preset.id) ? (
                        <img
                          src={effectThumbnails.get(preset.id)}
                          alt={preset.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <svg
                          className="w-5 h-5 text-muted-foreground"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14"
                          />
                        </svg>
                      )}
                    </div>
                    <span className="truncate w-full text-center">{preset.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* Tools — Background Removal */}
        <TabsContent value="tools" className="p-4">
          <BackgroundRemoval
            onApply={(blobUrl) => {
              if (!selectedArtwork) return;
              // Replace the artwork image with the background-removed version
              // Convert blob URL to a File-like object and store it
              fetch(blobUrl)
                .then((res) => res.blob())
                .then((blob) => {
                  const file = new File([blob], 'bg-removed.png', { type: 'image/png' });
                  storeLocalImage(selectedArtwork.artworkId, file);
                });
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
